/*
  Nebula accordions: smooth open / close for native <details data-nebula-accordion>.
  Used by the product page info + setup accordions (sections/nebula-product.liquid) and the FAQ
  (sections/nebula-pdp-faq.liquid).
  - Height eases between the summary and the full content; the content fades in / out.
  - Clicking again mid-animation reverses from the current height (no jump).
  - A container with data-accordion-single keeps one item open at a time.
  - While closing, the item carries .is-closing so icons (+/-, chevron) turn back straight away.
  - Works without JS (plain <details>). Reduced motion: opens and closes instantly.
  One delegated listener on document, registered once, so theme-editor section reloads need no re-init.
*/
(() => {
  if (window.__nebulaAccordion) return;
  window.__nebulaAccordion = true;

  const SELECTOR = 'details[data-nebula-accordion]';
  const OPEN_MS = 340;
  const CLOSE_MS = 260;
  const EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const running = new WeakMap();

  const content = (item) => Array.from(item.children).filter((el) => el.tagName !== 'SUMMARY');

  // Real closed height (summary margins, borders and open-only styles included): close for one
  // synchronous layout, measure, reopen. Nothing is painted in between.
  const closedHeight = (item) => {
    item.open = false;
    const h = item.getBoundingClientRect().height;
    item.open = true;
    return h;
  };

  const finish = (item) => {
    running.delete(item);
    item.style.removeProperty('height');
    item.style.removeProperty('overflow');
  };

  function toggle(item, opening) {
    if (reduce.matches) {
      item.classList.remove('is-closing');
      item.open = opening;
      return;
    }
    const startHeight = item.getBoundingClientRect().height;
    const current = running.get(item);
    if (current) {
      current.forEach((a) => a.cancel());
      running.delete(item);
    }

    item.classList.toggle('is-closing', !opening);
    if (opening) item.open = true;
    item.style.removeProperty('height');
    const endHeight = opening ? item.getBoundingClientRect().height : closedHeight(item);

    item.style.overflow = 'hidden';
    const duration = opening ? OPEN_MS : CLOSE_MS;
    const animations = [
      item.animate({ height: [startHeight + 'px', endHeight + 'px'] }, { duration, easing: EASE }),
      ...content(item).map((el) =>
        el.animate(
          opening
            ? { opacity: [0, 1], transform: ['translateY(-6px)', 'translateY(0)'] }
            : { opacity: [1, 0] },
          { duration: opening ? duration : duration * 0.7, easing: EASE, fill: opening ? 'none' : 'forwards' }
        )
      ),
    ];
    running.set(item, animations);

    animations[0].onfinish = () => {
      if (!opening) {
        item.open = false;
        item.classList.remove('is-closing');
        animations.forEach((a) => a.cancel()); // drop the forwards-filled fade so content is visible next time
      }
      finish(item);
    };
  }

  const isOpen = (item) => item.open && !item.classList.contains('is-closing');

  function open(item) {
    if (isOpen(item)) return;
    const group = item.closest('[data-accordion-single]');
    if (group) {
      group.querySelectorAll(SELECTOR).forEach((other) => {
        if (other !== item && isOpen(other)) toggle(other, false);
      });
    }
    toggle(item, true);
  }

  document.addEventListener('click', (e) => {
    const summary = e.target.closest('summary');
    if (!summary) return;
    const item = summary.parentElement;
    if (!item || !item.matches(SELECTOR)) return;
    e.preventDefault();
    if (isOpen(item)) toggle(item, false);
    else open(item);
  });

  // Theme editor: selecting a block inside an accordion (or the accordion itself) opens it.
  document.addEventListener('shopify:block:select', (e) => {
    const item = e.target.closest(SELECTOR) || (e.target.querySelector && e.target.querySelector(SELECTOR));
    if (item && !isOpen(item)) {
      item.classList.remove('is-closing');
      item.open = true;
    }
  });
})();
